// TelcoTrack — Věcná břemena: katalog CETIN vzorů smluv (Projekce/VZORY/smlouvy, revize 030226 / 160725).
//
// Zdroje textů:
// - 4.x SoSB a 3.x SK: „komplet“ vzory (všechny varianty + pokyny v komentářích) → skládané klauzule;
// - 2.x SUZ: potvrzená CETIN HTML šablona (pozemek) + varianty budova / pozemek+budova z CETIN vzorů;
// - 2.4 protokol: vzor protokolu o rozsahu umístění (příloha SUZ);
// - 5.0 a trojstranná VPI: text zatím není v repu ani na dostupném disku → stub.

import type {
  BremenaObjectKind,
  BremenaTemplateCatalogEntry,
  BremenaTemplateFamily,
  BremenaTemplateVariantId,
} from './template-catalog.types';

const SLUZEBNOST_TYPES = ['sk_konecna', 'zrizeni_sluzebnosti'] as const;

export const BREMENA_TEMPLATE_CATALOG: readonly BremenaTemplateCatalogEntry[] = [
  // ——— 2.x SUZ — smlouva o umístění vedení a zařízení veřejné komunikační sítě ———
  {
    id: 'suz_2_0_komplet',
    family: 'suz',
    code: '2.0',
    labelCs: 'SUZ — komplet (všechny varianty)',
    sourceFile: '2.0-suz-komplet-vzor',
    revision: '030226',
    source: 'master',
    instrumentTypes: ['suz'],
    objectKind: null,
    zapsanoVKn: true,
    representative: 'protelco',
    derivedFrom: null,
    noteCs: 'Zdroj klauzulí pro 2.1–2.3; nikdy se negeneruje protistraně.',
  },
  {
    id: 'suz_2_0_komplet_nova_optika',
    family: 'suz',
    code: '2.0',
    labelCs: 'SUZ — komplet s hlavičkou Nova Optika s.r.o.',
    sourceFile: '2.0-suz-komplet-vzor-s-hlavickou-pro-nova-optika-s.r.o',
    revision: '030226',
    source: 'master',
    instrumentTypes: ['suz'],
    objectKind: null,
    zapsanoVKn: true,
    representative: 'nova_optika',
    derivedFrom: 'suz_2_0_komplet',
    noteCs: 'Totožný text; liší se zmocněncem CETIN v hlavičce a podpisech.',
  },
  {
    id: 'suz_2_1_pozemek',
    family: 'suz',
    code: '2.1',
    labelCs: 'SUZ — pozemek',
    sourceFile: '2.1-suz-pozemek-vzor',
    revision: '030226',
    source: 'html_v1',
    instrumentTypes: ['suz'],
    objectKind: 'pozemek',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'suz_2_0_komplet',
    noteCs: 'Potvrzená CETIN HTML šablona `suz/v1/SUZ.html` (Mustache sloty).',
  },
  {
    id: 'suz_2_2_budova',
    family: 'suz',
    code: '2.2',
    labelCs: 'SUZ — budova',
    sourceFile: '2.2-suz-budova-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: ['suz'],
    objectKind: 'budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'suz_2_0_komplet',
  },
  {
    id: 'suz_2_3_pozemek_budova_jednotka',
    family: 'suz',
    code: '2.3',
    labelCs: 'SUZ — pozemek + budova (+ jednotky)',
    sourceFile: '2.3-suz-pozemek-budova-jednotka-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: ['suz'],
    objectKind: 'pozemek_budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'suz_2_0_komplet',
    noteCs: 'Používá se i pro předmět „jednotka“ (SVJ / rozdělená budova).',
  },
  // ——— 2.4 Protokol o rozsahu umístění (příloha SUZ k ručnímu doplnění) ———
  {
    id: 'suz_protokol_2_4b_pozemek_budova',
    family: 'suz_protokol',
    code: '2.4b',
    labelCs: 'Protokol k SUZ — pozemek + budova',
    sourceFile: '2.4b-suz-priloha-protokol-k-rucnimu-doplneni-pozemek-budova',
    revision: '160725',
    source: 'composed',
    instrumentTypes: ['suz'],
    objectKind: 'pozemek_budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
  },
  {
    id: 'suz_protokol_2_4c_budova',
    family: 'suz_protokol',
    code: '2.4c',
    labelCs: 'Protokol k SUZ — jen budova',
    sourceFile: '2.4c-suz-priloha-protokol-k-rucnimu-doplneni-jen-budova-vzor',
    revision: '160725',
    source: 'composed',
    instrumentTypes: ['suz'],
    objectKind: 'budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
  },
  {
    id: 'suz_protokol_2_4d_pozemek',
    family: 'suz_protokol',
    code: '2.4d',
    labelCs: 'Protokol k SUZ — jen pozemek',
    sourceFile: '2.4d-suz-priloha-protokol-k-rucnimu-doplneni-jen-pozemek-vzor',
    revision: '160725',
    source: 'composed',
    instrumentTypes: ['suz'],
    objectKind: 'pozemek',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
  },
  // ——— 3.x SK — smlouva o zřízení služebnosti (konečná) ———
  {
    id: 'sk_3_0_komplet',
    family: 'sk',
    code: '3.0',
    labelCs: 'SK — komplet (všechny varianty)',
    sourceFile: '3.0-smlouva-o-zrizeni-sluzebnosti-komplet-vzor',
    revision: '030226',
    source: 'master',
    instrumentTypes: SLUZEBNOST_TYPES,
    objectKind: null,
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
    noteCs: 'Zdroj klauzulí pro 3.1–3.4.',
  },
  {
    id: 'sk_3_1_pozemek',
    family: 'sk',
    code: '3.1',
    labelCs: 'SK — pozemek',
    sourceFile: '3.1-smlouva-o-zrizeni-sluzebnosti-pozemek-vzor',
    revision: '030226',
    source: 'html_v1',
    instrumentTypes: SLUZEBNOST_TYPES,
    objectKind: 'pozemek',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'sk_3_0_komplet',
    noteCs: 'Potvrzená CETIN HTML šablona `sk_konecna/v1/SMLOUVA_KONECNA.html`.',
  },
  {
    id: 'sk_3_2_budova',
    family: 'sk',
    code: '3.2',
    labelCs: 'SK — budova',
    sourceFile: '3.2-smlouva-o-zrizeni-sluzebnosti-budova-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: SLUZEBNOST_TYPES,
    objectKind: 'budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'sk_3_0_komplet',
  },
  {
    id: 'sk_3_3_pozemek_budova',
    family: 'sk',
    code: '3.3',
    labelCs: 'SK — pozemek + budova',
    sourceFile: '3.3-smlouva-o-zrizeni-sluzebnosti-pozemek-budova-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: SLUZEBNOST_TYPES,
    objectKind: 'pozemek_budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'sk_3_0_komplet',
  },
  {
    id: 'sk_3_4_nezapsane_kn',
    family: 'sk',
    code: '3.4',
    labelCs: 'SK — předmět nezapsaný v KN',
    sourceFile: '3.4-smlouva-o-zrizeni-sluzebnosti-nezapsane-v-kn-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: SLUZEBNOST_TYPES,
    objectKind: 'budova',
    zapsanoVKn: false,
    representative: null,
    derivedFrom: 'sk_3_0_komplet',
    noteCs: 'Služebnost vzniká účinností smlouvy (§ 1262 OZ) — bez GP a návrhu na vklad.',
  },
  // ——— 4.x SoSB — smlouva o budoucí smlouvě o zřízení služebnosti ———
  {
    id: 'sosb_4_0_komplet',
    family: 'sosb',
    code: '4.0',
    labelCs: 'SoSB — komplet (všechny varianty)',
    sourceFile: '3.0-dotace-sosb-o-zrizeni-sluzebnosti-komplet-vzor',
    revision: '300326',
    source: 'master',
    instrumentTypes: ['sosb'],
    objectKind: null,
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
    noteCs: 'Přečten z dotační řady NGA (Zakázky/Zadání/CETIN/05_PARDUBICE/NGA_vzory); dotační odstavce se neskládají.',
  },
  {
    id: 'sosb_4_1_pozemek',
    family: 'sosb',
    code: '4.1',
    labelCs: 'SoSB — pozemek',
    sourceFile: '4.1-sosb-o-zrizeni-sluzebnosti-pozemek-vzor',
    revision: '030226',
    source: 'html_v1',
    instrumentTypes: ['sosb'],
    objectKind: 'pozemek',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'sosb_4_0_komplet',
    noteCs: 'Potvrzená CETIN HTML šablona `sosb/v1/SMLOUVA_BUDOUCI.html`.',
  },
  {
    id: 'sosb_4_2_budova',
    family: 'sosb',
    code: '4.2',
    labelCs: 'SoSB — budova',
    sourceFile: '4.2-sosb-o-zrizeni-sluzebnosti-budova-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: ['sosb'],
    objectKind: 'budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'sosb_4_0_komplet',
  },
  {
    id: 'sosb_4_3_pozemek_budova',
    family: 'sosb',
    code: '4.3',
    labelCs: 'SoSB — pozemek + budova',
    sourceFile: '4.3-sosb-o-zrizeni-sluzebnosti-pozemek-budova-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: ['sosb'],
    objectKind: 'pozemek_budova',
    zapsanoVKn: true,
    representative: null,
    derivedFrom: 'sosb_4_0_komplet',
  },
  {
    id: 'sosb_4_4_nezapsane_kn',
    family: 'sosb',
    code: '4.4',
    labelCs: 'SoSB — předmět nezapsaný v KN',
    sourceFile: '4.4-sosb-o-zrizeni-sluzebnosti-nezapsane-v-kn-vzor',
    revision: '030226',
    source: 'composed',
    instrumentTypes: ['sosb'],
    objectKind: 'budova',
    zapsanoVKn: false,
    representative: null,
    derivedFrom: 'sosb_4_0_komplet',
    noteCs: 'Budoucí služebnost vznikne účinností budoucí smlouvy — bez GP a vkladu.',
  },
  // ——— 5.0 Dohoda o zrušení služebnosti ———
  {
    id: 'zruseni_5_0',
    family: 'zruseni',
    code: '5.0',
    labelCs: 'Dohoda o zrušení služebnosti',
    sourceFile: '5.0-dohoda-o-zruseni-sluzebnosti-vzor',
    revision: '160725',
    source: 'unavailable',
    instrumentTypes: SLUZEBNOST_TYPES,
    objectKind: null,
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
    noteCs: 'Text vzoru není dostupný (disk Projekce). Vyžaduje nový typ nástroje / dokumentu — produktové rozhodnutí.',
  },
  // ——— Trojstranná smlouva VPI (vbr-sb-trojstrannavpi.doc) ———
  {
    id: 'vpi_trojstranna',
    family: 'vpi',
    code: 'VPI',
    labelCs: 'Trojstranná smlouva o služebnosti (VPI)',
    sourceFile: 'vbr-sb-trojstrannavpi',
    revision: null,
    source: 'unavailable',
    instrumentTypes: ['sosb', ...SLUZEBNOST_TYPES],
    objectKind: null,
    zapsanoVKn: true,
    representative: null,
    derivedFrom: null,
    noteCs: 'Legacy .doc; text nedostupný. Třetí strana (investor VPI) v modelu nástroje chybí — stub.',
  },
];

const CATALOG_BY_ID = new Map(BREMENA_TEMPLATE_CATALOG.map((e) => [e.id, e]));

export function getBremenaTemplateVariant(
  id: BremenaTemplateVariantId | string | null | undefined,
): BremenaTemplateCatalogEntry | null {
  const key = (id ?? '').trim();
  return key ? CATALOG_BY_ID.get(key as BremenaTemplateVariantId) ?? null : null;
}

export function isBremenaTemplateVariantId(value: string | null | undefined): value is BremenaTemplateVariantId {
  return CATALOG_BY_ID.has((value ?? '').trim() as BremenaTemplateVariantId);
}

/** Varianty, které lze reálně vygenerovat (ne komplet / stub). */
export function isBremenaTemplateGeneratable(entry: BremenaTemplateCatalogEntry | null | undefined): boolean {
  return !!entry && (entry.source === 'html_v1' || entry.source === 'composed');
}

export function listBremenaTemplateVariants(filter?: {
  family?: BremenaTemplateFamily;
  instrumentType?: string | null;
  objectKind?: BremenaObjectKind;
  generatableOnly?: boolean;
}): BremenaTemplateCatalogEntry[] {
  const type = (filter?.instrumentType ?? '').trim();
  return BREMENA_TEMPLATE_CATALOG.filter((e) => {
    if (filter?.family && e.family !== filter.family) return false;
    if (type && !(e.instrumentTypes as readonly string[]).includes(type)) return false;
    if (filter?.objectKind && e.objectKind !== null && e.objectKind !== filter.objectKind) return false;
    if (filter?.generatableOnly && !isBremenaTemplateGeneratable(e)) return false;
    return true;
  });
}

/** Rodina vzorů pro typ nástroje (zrizeni_sluzebnosti sdílí SK). */
export function bremenaTemplateFamilyForInstrument(
  instrumentType: string | null | undefined,
): Exclude<BremenaTemplateFamily, 'suz_protokol' | 'zruseni' | 'vpi'> | null {
  switch ((instrumentType ?? '').trim()) {
    case 'suz':
      return 'suz';
    case 'sosb':
      return 'sosb';
    case 'sk_konecna':
    case 'zrizeni_sluzebnosti':
      return 'sk';
    default:
      return null;
  }
}
